﻿import {
  StringDevtoolDefinition,
  StringDevtoolState,
  StringDevtoolSubBadge,
} from "../../../models/devtools/StringDevtool";
import { makeIconButton } from "./StringDevElements";
import { resolveDevtoolsIcon } from "./StringDevIconRegistry";
import { ensureStringDevtoolsSharedStyles } from "./StringDevStyleSystem";
import { getStringDevViewportPolicy } from "./StringDevViewportPolicy";
import { scopeStringDevStorageKey } from "./StringDevStorageScope";

interface DockEntry {
  definition: StringDevtoolDefinition;
  button: HTMLButtonElement;
  slot: HTMLDivElement;
  subBadges: HTMLDivElement | null;
  unsubscribe: (() => void) | null;
  cleanup: Array<() => void>;
}

interface DockPreferences {
  collapsed: boolean;
  active: Record<string, boolean>;
}

const DEVTOOLS_DOCK_STORAGE_KEY = "string-devtools:dock";
const SUB_BADGE_LONG_PRESS_DELAY_MS = 600;
const SUB_BADGE_LONG_PRESS_MOVE_TOLERANCE = 12;
const DOCK_ROOT_SELECTOR = "[data-stdg-dock]";
const SUB_BADGE_GROUP_SELECTOR = "[data-stdg-dock-sub-badges]";

function formatHotkey(hotkey: StringDevtoolDefinition["hotkey"]): string {
  if (!hotkey) return "";

  const parts: string[] = [];
  if (hotkey.ctrlKey) parts.push("Ctrl");
  if (hotkey.altKey) parts.push("Alt");
  if (hotkey.shiftKey) parts.push("Shift");
  if (hotkey.metaKey) parts.push("Meta");
  parts.push(hotkey.key.length === 1 ? hotkey.key.toUpperCase() : hotkey.key);
  return parts.join("+");
}

export class StringDevtoolsDock {
  private root: HTMLDivElement;
  private mainButton: HTMLButtonElement;
  private itemsWrap: HTMLDivElement;
  private toolsWrap: HTMLDivElement;
  private fpsSeparator: HTMLSpanElement;
  private fpsElement: HTMLDivElement;
  private entries: Map<string, DockEntry> = new Map();
  private collapsed: boolean = false;
  private suppressPersist: boolean = false;
  private readonly preferences: DockPreferences;
  private readonly onKeydownBind: (event: KeyboardEvent) => void;
  private readonly onResizeBind: () => void;

  constructor() {
    ensureStringDevtoolsSharedStyles();
    this.cleanupExistingDockArtifacts();
    this.preferences = this.loadPreferences();
    this.onResizeBind = () => {
      window.requestAnimationFrame(() => this.handleViewportChange());
    };
    this.onKeydownBind = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement ||
          target.isContentEditable)
      ) {
        return;
      }
      if (
        event.shiftKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.metaKey &&
        event.code === "KeyS"
      ) {
        event.preventDefault();
        this.setCollapsed(!this.collapsed);
      }
    };
    window.addEventListener("keydown", this.onKeydownBind);
    window.addEventListener("resize", this.onResizeBind);
    this.root = document.createElement("div");
    this.root.setAttribute("data-stdg", "");
    this.root.setAttribute("data-stdg-dock", "");
    this.root.setAttribute("data-collapsed", this.preferences.collapsed ? "true" : "false");
    this.mainButton = this.createMainButton();
    this.itemsWrap = document.createElement("div");
    this.itemsWrap.setAttribute("data-stdg-dock-list", "");
    this.toolsWrap = document.createElement("div");
    this.toolsWrap.setAttribute("data-stdg-dock-tools", "");
    this.fpsSeparator = this.createSeparator();
    this.fpsSeparator.setAttribute("data-stdg-dock-fps-separator", "");
    this.fpsElement = document.createElement("div");
    this.fpsElement.setAttribute("data-string-defguides-doc-fps", "");
    this.fpsElement.title = "Current FPS";
    const fpsSpan = document.createElement("span");
    fpsSpan.textContent = "0";
    this.fpsElement.appendChild(fpsSpan);
    this.root.appendChild(this.mainButton);
    this.itemsWrap.appendChild(this.toolsWrap);
    this.itemsWrap.appendChild(this.fpsSeparator);
    this.itemsWrap.appendChild(this.fpsElement);
    this.root.appendChild(this.itemsWrap);
    (document.body ?? document.documentElement).appendChild(this.root);
    this.setCollapsed(this.preferences.collapsed, false);
  }

  add(definition: StringDevtoolDefinition): void {
    if (this.entries.has(definition.id)) {
      return;
    }

    this.applyStoredActiveState(definition);

    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("data-stdg", "");
    button.setAttribute("data-stdg-button", "");
    button.setAttribute("data-stdg-button-icon-20", "");
    button.setAttribute("data-devguides-id", definition.id);
    const hotkeyLabel = formatHotkey(definition.hotkey);
    const buttonLabel = hotkeyLabel ? `${definition.label} (${hotkeyLabel})` : definition.label;
    button.setAttribute("aria-label", buttonLabel);
    button.innerHTML = resolveDevtoolsIcon(20, definition.icon);
    button.title = buttonLabel;
    button.addEventListener("click", () => {
      const nextActive = !definition.getState().active;
      definition.setActive(nextActive);
      if (!definition.subscribe) {
        const nextState = definition.getState();
        this.renderButton(button, definition.label, definition.hotkey, nextState);
        this.persistActiveState(definition.id, nextState.active);
      }
    });

    const slot = document.createElement("div");
    slot.setAttribute("data-stdg", "");
    slot.setAttribute("data-stdg-dock-slot", "");
    slot.setAttribute("data-devguides-slot", definition.id);
    slot.appendChild(button);

    let subBadges: HTMLDivElement | null = null;
    if (definition.subBadges && definition.subBadges.length > 0) {
      slot.setAttribute("data-has-sub-badges", "");
      subBadges = this.createSubBadgeGroup(definition);
    }

    const cleanup: Array<() => void> = [];
    if (subBadges) {
      cleanup.push(...this.attachSubBadges(slot, subBadges));
    }

    const unsubscribe = definition.subscribe
      ? definition.subscribe((state) => {
          this.renderButton(button, definition.label, definition.hotkey, state);
          this.syncSubBadgeState(slot, subBadges, state.active);
          this.persistActiveState(definition.id, state.active);
        })
      : null;

    this.entries.set(definition.id, { definition, button, slot, subBadges, unsubscribe, cleanup });
    this.toolsWrap.appendChild(slot);
    this.sortButtons();
    const currentState = definition.getState();
    this.renderButton(button, definition.label, definition.hotkey, currentState);
    this.syncSubBadgeState(slot, subBadges, currentState.active);
    this.persistActiveState(definition.id, currentState.active);
  }

  private attachSubBadges(slot: HTMLDivElement, group: HTMLDivElement): Array<() => void> {
    const cleanup: Array<() => void> = [];
    (document.body ?? document.documentElement).appendChild(group);

    let open = false;
    let longPressTimer: number | null = null;
    let longPressPointerId: number | null = null;
    let longPressStartX = 0;
    let longPressStartY = 0;
    let suppressNextClick = false;
    const setOpen = (next: boolean) => {
      if (open === next) return;
      open = next;
      group.setAttribute("data-open", next ? "true" : "false");
      if (next) this.positionSubBadges(slot, group);
    };

    const isCompact = () => getStringDevViewportPolicy(window.innerWidth).compact;
    const clearLongPress = () => {
      if (longPressTimer !== null) {
        window.clearTimeout(longPressTimer);
        longPressTimer = null;
      }
      longPressPointerId = null;
    };
    const releaseLongPress = () => {
      clearLongPress();
      if (!suppressNextClick) return;
      window.setTimeout(() => {
        suppressNextClick = false;
      }, 0);
    };

    const scheduleClose = () => {
      window.setTimeout(() => {
        if (slot.matches(":hover") || group.matches(":hover")) return;
        setOpen(false);
      }, 60);
    };

    const onSlotPointerEnter = () => {
      if (!isCompact()) setOpen(true);
    };
    slot.addEventListener("pointerenter", onSlotPointerEnter);
    cleanup.push(() => slot.removeEventListener("pointerenter", onSlotPointerEnter));
    const onSlotPointerLeave = () => {
      if (!isCompact()) scheduleClose();
    };
    slot.addEventListener("pointerleave", onSlotPointerLeave);
    cleanup.push(() => slot.removeEventListener("pointerleave", onSlotPointerLeave));
    const onGroupPointerEnter = () => {
      if (!isCompact()) setOpen(true);
    };
    group.addEventListener("pointerenter", onGroupPointerEnter);
    cleanup.push(() => group.removeEventListener("pointerenter", onGroupPointerEnter));
    const onGroupPointerLeave = () => {
      if (!isCompact()) scheduleClose();
    };
    group.addEventListener("pointerleave", onGroupPointerLeave);
    cleanup.push(() => group.removeEventListener("pointerleave", onGroupPointerLeave));
    const onSubBadgeAction = () => setOpen(false);
    group.addEventListener("string-devtools-sub-badge-action", onSubBadgeAction);
    cleanup.push(() => group.removeEventListener("string-devtools-sub-badge-action", onSubBadgeAction));
    const onSlotPointerDown = (event: PointerEvent) => {
      if (!isCompact()) return;
      if (
        event.target instanceof HTMLElement &&
        event.target.closest("[data-stdg-dock-sub-badge]")
      ) {
        return;
      }
      clearLongPress();
      longPressPointerId = event.pointerId;
      longPressStartX = event.clientX;
      longPressStartY = event.clientY;
      longPressTimer = window.setTimeout(() => {
        longPressTimer = null;
        suppressNextClick = true;
        setOpen(true);
      }, SUB_BADGE_LONG_PRESS_DELAY_MS);
    };
    slot.addEventListener("pointerdown", onSlotPointerDown);
    cleanup.push(() => slot.removeEventListener("pointerdown", onSlotPointerDown));
    const onSlotPointerMove = (event: PointerEvent) => {
      if (!isCompact() || longPressTimer === null || event.pointerId !== longPressPointerId) {
        return;
      }
      const distanceX = Math.abs(event.clientX - longPressStartX);
      const distanceY = Math.abs(event.clientY - longPressStartY);
      if (
        distanceX > SUB_BADGE_LONG_PRESS_MOVE_TOLERANCE ||
        distanceY > SUB_BADGE_LONG_PRESS_MOVE_TOLERANCE
      ) {
        clearLongPress();
      }
    };
    slot.addEventListener("pointermove", onSlotPointerMove);
    cleanup.push(() => slot.removeEventListener("pointermove", onSlotPointerMove));
    slot.addEventListener("pointerup", releaseLongPress);
    cleanup.push(() => slot.removeEventListener("pointerup", releaseLongPress));
    slot.addEventListener("pointercancel", releaseLongPress);
    cleanup.push(() => slot.removeEventListener("pointercancel", releaseLongPress));
    const onSlotPointerLeaveCompact = () => {
      if (isCompact()) clearLongPress();
    };
    slot.addEventListener("pointerleave", onSlotPointerLeaveCompact);
    cleanup.push(() => slot.removeEventListener("pointerleave", onSlotPointerLeaveCompact));
    const onSlotContextMenu = (event: Event) => {
      if (!isCompact() || !suppressNextClick) return;
      event.preventDefault();
    };
    slot.addEventListener("contextmenu", onSlotContextMenu);
    cleanup.push(() => slot.removeEventListener("contextmenu", onSlotContextMenu));
    const onSlotSelectStart = (event: Event) => {
      if (!isCompact()) return;
      event.preventDefault();
    };
    slot.addEventListener("selectstart", onSlotSelectStart);
    cleanup.push(() => slot.removeEventListener("selectstart", onSlotSelectStart));
    const onSlotClickCapture = (event: Event) => {
      if (!isCompact() || !suppressNextClick) return;
      suppressNextClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    slot.addEventListener("click", onSlotClickCapture, true);
    cleanup.push(() => slot.removeEventListener("click", onSlotClickCapture, true));
    group.addEventListener("pointerdown", releaseLongPress);
    cleanup.push(() => group.removeEventListener("pointerdown", releaseLongPress));
    group.addEventListener("pointercancel", releaseLongPress);
    cleanup.push(() => group.removeEventListener("pointercancel", releaseLongPress));
    const onGroupContextMenu = (event: Event) => {
      if (!isCompact()) return;
      event.preventDefault();
    };
    group.addEventListener("contextmenu", onGroupContextMenu);
    cleanup.push(() => group.removeEventListener("contextmenu", onGroupContextMenu));
    const onGroupSelectStart = (event: Event) => {
      if (!isCompact()) return;
      event.preventDefault();
    };
    group.addEventListener("selectstart", onGroupSelectStart);
    cleanup.push(() => group.removeEventListener("selectstart", onGroupSelectStart));
    const onGroupClick = () => {
      suppressNextClick = false;
    };
    group.addEventListener("click", onGroupClick);
    cleanup.push(() => group.removeEventListener("click", onGroupClick));
    const onDocumentPointerDown = (event: PointerEvent) => {
      if (!open) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (slot.contains(target) || group.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDocumentPointerDown);
    cleanup.push(() => document.removeEventListener("pointerdown", onDocumentPointerDown));

    // Watches an attribute change, not childList/subtree like ObjectManager's watcher — a different subscription, not a duplicate.
    const activeObserver = new MutationObserver(() => {
      if (!group.querySelector('[data-active="true"]')) scheduleClose();
    });
    activeObserver.observe(group, {
      subtree: true,
      attributes: true,
      attributeFilter: ["data-active"],
    });
    cleanup.push(() => activeObserver.disconnect());

    const reposition = () => {
      if (open) this.positionSubBadges(slot, group);
    };
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    cleanup.push(() => window.removeEventListener("resize", reposition));
    cleanup.push(() => window.removeEventListener("scroll", reposition, true));

    group.setAttribute("data-open", "false");
    cleanup.push(() => group.remove());
    return cleanup;
  }

  private positionSubBadges(slot: HTMLDivElement, group: HTMLDivElement): void {
    const rect = slot.getBoundingClientRect();
    if (getStringDevViewportPolicy(window.innerWidth).compact) {
      group.style.left = `${Math.round(rect.right + 8)}px`;
      group.style.top = `${Math.round(rect.top + rect.height / 2)}px`;
      group.style.bottom = "";
    } else {
      group.style.left = `${Math.round(rect.left + rect.width / 2)}px`;
      group.style.top = "";
      group.style.bottom = `${Math.round(window.innerHeight - rect.top + 8)}px`;
    }
  }

  private createSubBadgeGroup(definition: StringDevtoolDefinition): HTMLDivElement {
    const group = document.createElement("div");
    group.setAttribute("data-stdg", "");
    group.setAttribute("data-stdg-dock-sub-badges", "");
    group.setAttribute("data-parent-active", "false");
    for (const descriptor of definition.subBadges ?? []) {
      const attrs: Record<string, string> = {
        "data-stdg": "",
        "data-stdg-dock-sub-badge": "",
        "data-sub-badge-id": descriptor.id,
        "data-active": "false",
        "data-parent-active": "false",
      };
      if (descriptor.selectorAttribute) {
        attrs[descriptor.selectorAttribute] = "";
      }
      if (descriptor.attributes) {
        for (const [key, value] of Object.entries(descriptor.attributes)) {
          if (value === null || value === undefined || value === false) continue;
          attrs[key] = value === true ? "" : String(value);
        }
      }
      const badge = makeIconButton({
        icon: descriptor.icon,
        size: 16,
        label: descriptor.label,
        modifiers: ["toggle"],
        attrs,
      });
      badge.addEventListener("pointerdown", (event) => {
        event.stopPropagation();
      });
      badge.addEventListener("click", (event) => {
        event.stopPropagation();
        if (!definition.getState().active) {
          definition.setActive(true);
        }
        descriptor.onClick(badge);
        badge.setAttribute("data-active", "false");
        badge.dispatchEvent(new CustomEvent("string-devtools-sub-badge-action", { bubbles: true }));
      });
      group.appendChild(badge);
    }
    return group;
  }

  remove(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;

    entry.unsubscribe?.();
    for (const cleanup of entry.cleanup) cleanup();
    entry.slot.remove();
    this.entries.delete(id);
  }

  destroy(): void {
    window.removeEventListener("keydown", this.onKeydownBind);
    window.removeEventListener("resize", this.onResizeBind);
    for (const entry of this.entries.values()) {
      entry.unsubscribe?.();
      for (const cleanup of entry.cleanup) cleanup();
      entry.slot.remove();
    }
    this.entries.clear();
    this.root.remove();
  }

  private cleanupExistingDockArtifacts(): void {
    document.querySelectorAll(DOCK_ROOT_SELECTOR).forEach((node) => node.remove());
    document.querySelectorAll(SUB_BADGE_GROUP_SELECTOR).forEach((node) => node.remove());
  }

  setFPS(fps: number): void {
    const span = this.fpsElement.querySelector("span");
    if (span) span.textContent = String(Math.max(0, Math.round(fps)));
  }

  private sortButtons(): void {
    const ordered = Array.from(this.entries.values()).sort((left, right) => {
      const leftOrder = left.definition.order ?? 0;
      const rightOrder = right.definition.order ?? 0;
      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }
      return left.definition.label.localeCompare(right.definition.label);
    });

    this.toolsWrap.innerHTML = "";
    this.toolsWrap.appendChild(this.createSeparator());

    let lastGroup: number | undefined = undefined;
    for (const entry of ordered) {
      const currentGroup = entry.definition.group;
      if (lastGroup !== undefined && currentGroup !== lastGroup) {
        this.toolsWrap.appendChild(this.createSeparator());
      }
      lastGroup = currentGroup;
      this.toolsWrap.appendChild(entry.slot);
    }

    this.fpsSeparator.style.display = ordered.length > 0 ? "block" : "none";
    this.syncCollapsedLayout();
  }

  private createSeparator(): HTMLSpanElement {
    const sep = document.createElement("span");
    sep.setAttribute("data-stdg-horizontal-line", "");
    return sep;
  }

  private createMainButton(): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("data-stdg", "");
    button.setAttribute("data-stdg-button", "");
    button.setAttribute("data-stdg-button-hover", "");
    button.setAttribute("data-stdg-button-icon-20", "");
    button.setAttribute("data-collapsed", this.preferences.collapsed ? "true" : "false");
    button.setAttribute("aria-label", "Toggle Dev Guides");
    button.innerHTML = `
      ${resolveDevtoolsIcon(20, "logo")}
      ${resolveDevtoolsIcon(12, "chevrone-up")}
    `;
    button.addEventListener("click", () => {
      this.setCollapsed(!this.collapsed);
    });
    return button;
  }

  private setCollapsed(collapsed: boolean, persist: boolean = true): void {
    this.collapsed = collapsed;
    this.root.setAttribute("data-collapsed", collapsed ? "true" : "false");
    this.mainButton.setAttribute("data-collapsed", collapsed ? "true" : "false");
    this.mainButton.setAttribute(
      "aria-label",
      collapsed ? "Expand developer tools" : "Collapse developer tools",
    );
    if (collapsed) {
      this.suppressPersist = true;
      for (const entry of this.entries.values()) {
        if (entry.definition.getState().active) {
          entry.definition.setActive(false);
        }
      }
      this.suppressPersist = false;
    } else {
      this.suppressPersist = true;
      for (const entry of this.entries.values()) {
        const saved = this.preferences.active[entry.definition.id];
        if (saved === true) {
          entry.definition.setActive(true);
        }
      }
      this.suppressPersist = false;
    }
    if (persist) {
      this.preferences.collapsed = collapsed;
      this.savePreferences();
    }
    this.syncCollapsedLayout();
  }

  private syncCollapsedLayout(): void {
    const compact = getStringDevViewportPolicy(window.innerWidth).compact;
    const contentSize = compact ? this.toolsWrap.scrollHeight : this.toolsWrap.scrollWidth;
    this.toolsWrap.style.maxHeight = compact ? (this.collapsed ? "0px" : `${contentSize}px`) : "";
    this.toolsWrap.style.maxWidth = compact ? "" : this.collapsed ? "0px" : `${contentSize}px`;
    this.toolsWrap.style.opacity = this.collapsed ? "0" : "1";
    this.toolsWrap.style.pointerEvents = this.collapsed ? "none" : "auto";
  }

  private handleViewportChange(): void {
    this.syncCollapsedLayout();
    for (const entry of this.entries.values()) {
      if (!entry.subBadges) continue;
      if (entry.subBadges.getAttribute("data-open") !== "true") continue;
      this.positionSubBadges(entry.slot, entry.subBadges);
    }
  }

  private renderButton(
    button: HTMLButtonElement,
    label: string,
    hotkey: StringDevtoolDefinition["hotkey"],
    state: StringDevtoolState,
  ): void {
    const hotkeyLabel = formatHotkey(hotkey);
    const labelWithHotkey = hotkeyLabel ? `${label} (${hotkeyLabel})` : label;
    button.setAttribute("data-active", state.active ? "true" : "false");
    button.setAttribute("aria-label", `${labelWithHotkey}: ${state.active ? "On" : "Off"}`);
    button.title = `${labelWithHotkey}: ${state.active ? "On" : "Off"}`;
  }

  private syncSubBadgeState(
    slot: HTMLDivElement,
    group: HTMLDivElement | null,
    active: boolean,
  ): void {
    const value = active ? "true" : "false";
    slot.setAttribute("data-active", value);
    if (!group) return;

    group.setAttribute("data-parent-active", value);
    for (const badge of group.querySelectorAll<HTMLElement>("[data-stdg-dock-sub-badge]")) {
      badge.setAttribute("data-parent-active", value);
      badge.setAttribute("aria-disabled", active ? "false" : "true");
    }
  }

  private applyStoredActiveState(definition: StringDevtoolDefinition): void {
    const saved = this.preferences.active[definition.id];
    if (typeof saved !== "boolean") {
      return;
    }

    // When dock is collapsed, never activate modules — they'll be restored on expand.
    const targetActive = this.collapsed ? false : saved;
    if (targetActive === definition.getState().active) {
      return;
    }

    definition.setActive(targetActive);
  }

  private persistActiveState(id: string, active: boolean): void {
    if (this.suppressPersist) return;
    // When collapsed, the modules are force-deactivated — don't overwrite the
    // stored "true" entries that need to be restored when expanding.
    if (this.collapsed) return;
    this.preferences.active[id] = active;
    this.savePreferences();
  }

  private loadPreferences(): DockPreferences {
    try {
      const raw = localStorage.getItem(this.dockStorageKey);
      if (!raw) {
        return { collapsed: false, active: {} };
      }

      const parsed = JSON.parse(raw) as Partial<DockPreferences>;
      return {
        collapsed: parsed.collapsed === true,
        active: parsed.active && typeof parsed.active === "object" ? parsed.active : {},
      };
    } catch {
      return { collapsed: false, active: {} };
    }
  }

  private savePreferences(): void {
    try {
      localStorage.setItem(this.dockStorageKey, JSON.stringify(this.preferences));
    } catch {
      // localStorage may be unavailable
    }
  }

  private get dockStorageKey(): string {
    return scopeStringDevStorageKey(DEVTOOLS_DOCK_STORAGE_KEY);
  }
}
